/**
 * nss-scrolling-bar — smooth JS marquee.
 *
 * Clones the items row (capped) to fill the viewport, then translates the
 * wrapper on the GPU each frame. Safety + performance notes:
 *  - Clone count is HARD-CAPPED and the set must measure a sane minimum width
 *    before cloning — otherwise a 0/near-0 measurement makes `need` explode and
 *    freezes the tab (that was the "Page Unresponsive" hang).
 *  - Media (eager images / video preload) is deferred until the bar is near the
 *    viewport, so this far-down section doesn't slow initial page load.
 *  - No per-frame DOM work → no flicker.
 *
 * DOM (nss-scrolling-bar.liquid):
 *   <nss-scrolling-bar data-scrolling-speed data-scrolling-direction data-pause-on-hover>
 *     <div class="js-nss-scrolling-bar-wrapper">
 *       <div class="nss-scrolling-bar__items"> … one set … </div>
 *     </div>
 *     [optional] <button class="js-nss-scrolling-bar-toggle">
 */
if (typeof customElements === "object" && !customElements.get("nss-scrolling-bar")) {
  var NSS_SB_MAX_CLONES = 12;
  var NSS_SB_MIN_SET = 40;

  class NSSScrollingBar extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.querySelector(".js-nss-scrolling-bar-wrapper");
      this.toggle = this.querySelector(".js-nss-scrolling-bar-toggle");
      if (!this.wrapper || !this.wrapper.children.length) return;

      this.original = this.wrapper.children[0];
      this.direction = this.dataset.scrollingDirection || "ltr";
      this.pauseOnHover = this.dataset.pauseOnHover === "true";
      this.baseSpeed = parseInt(this.dataset.scrollingSpeed, 10) || 40;

      this.position = 0;
      this.setWidth = 0;
      this.last = null;
      this._raf = 0;
      this._activated = false;

      this.hover = false;
      this.hidden = typeof document !== "undefined" && document.hidden;
      this.offscreen = false;
      this.userPaused = false;

      this._frame = this.frame.bind(this);

      this.watchLoads();
      this.bindEvents();
      if (typeof IntersectionObserver !== "function") this.activate();
    }

    disconnectedCallback() {
      if (this._raf) cancelAnimationFrame(this._raf);
      clearTimeout(this._setupTimer);
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
    }

    get speed() {
      return window.innerWidth > 768 ? this.baseSpeed : this.baseSpeed / 1.5;
    }

    activate() {
      if (this._activated) return;
      this._activated = true;
      this.hintMedia();
      this.setup();
    }

    hintMedia() {
      this.original.querySelectorAll("img").forEach((img) => {
        img.loading = "eager";
        img.decoding = "async";
      });
      this.original.querySelectorAll("video").forEach((v) => {
        v.preload = "auto";
        v.muted = true;
        v.setAttribute("playsinline", "");
        const p = v.play && v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      });
    }

    setup() {
      const w = this.original.getBoundingClientRect().width;
      if (!w || w < NSS_SB_MIN_SET) {
        clearTimeout(this._setupTimer);
        this._setupTimer = setTimeout(() => this.setup(), 120);
        return;
      }
      this.setWidth = w;
      const viewport = this.getBoundingClientRect().width || window.innerWidth;
      let need = Math.ceil((viewport * 2) / w) + 1;
      if (need > NSS_SB_MAX_CLONES) need = NSS_SB_MAX_CLONES;
      if (need < 2) need = 2;
      let guard = 0;
      while (this.wrapper.children.length < need && guard++ < NSS_SB_MAX_CLONES) {
        const clone = this.original.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        this.wrapper.appendChild(clone);
      }
      if (this.position >= this.setWidth) this.position %= this.setWidth;
      this.update();
    }

    watchLoads() {
      const remeasure = () => {
        if (!this._activated) return;
        const w = this.original.getBoundingClientRect().width;
        if (w && w >= NSS_SB_MIN_SET) {
          this.setWidth = w;
          if (this.position >= this.setWidth) this.position %= this.setWidth;
          this.update();
        }
      };
      this.original.querySelectorAll("img").forEach((img) => {
        img.addEventListener("load", remeasure, { once: true });
      });
      this.original.querySelectorAll("video").forEach((v) => {
        v.addEventListener("loadedmetadata", remeasure, { once: true });
      });
      window.addEventListener("load", remeasure);
    }

    shouldRun() {
      return (
        this.setWidth > 0 &&
        !this.hover &&
        !this.hidden &&
        !this.offscreen &&
        !this.userPaused
      );
    }

    update() {
      const run = this.shouldRun();
      if (run && !this._raf) {
        this.last = null;
        this._raf = window.requestAnimationFrame(this._frame);
      } else if (!run && this._raf) {
        window.cancelAnimationFrame(this._raf);
        this._raf = 0;
      }
      if (this.toggle) {
        this.toggle.classList.toggle("is-plaing", !this.userPaused);
        this.toggle.setAttribute("aria-label", this.userPaused ? "Play" : "Pause");
      }
    }

    frame(now) {
      this._raf = 0;
      const dt = this.last === null ? 0 : now - this.last;
      this.last = now;
      this.position += (this.speed * dt) / 1000;
      if (this.setWidth > 0) {
        while (this.position >= this.setWidth) this.position -= this.setWidth;
      }
      const x = this.direction === "rtl" ? this.position : -this.position;
      this.wrapper.style.transform = "translate3d(" + x + "px, 0, 0)";
      if (this.shouldRun()) {
        this._raf = window.requestAnimationFrame(this._frame);
      }
    }

    bindEvents() {
      let t;
      const onResize = () => {
        clearTimeout(t);
        t = setTimeout(() => this._activated && this.setup(), 150);
      };
      if (typeof ResizeObserver === "function") {
        this._ro = new ResizeObserver(onResize);
        this._ro.observe(this);
      } else {
        window.addEventListener("resize", onResize);
      }

      document.addEventListener("visibilitychange", () => {
        this.hidden = document.hidden;
        this.update();
      });

      if (this.pauseOnHover) {
        this.addEventListener("mouseenter", () => {
          this.hover = true;
          this.update();
        });
        this.addEventListener("mouseleave", () => {
          this.hover = false;
          this.update();
        });
      }

      if (this.toggle) {
        this.toggle.addEventListener("click", () => {
          this.userPaused = !this.userPaused;
          this.update();
        });
      }

      if (typeof IntersectionObserver === "function") {
        this._io = new IntersectionObserver(
          (entries) => {
            this.offscreen = !entries[0].isIntersecting;
            if (!this.offscreen) this.activate();
            this.update();
          },
          { rootMargin: "200px" }
        );
        this._io.observe(this);
      }
    }
  }

  customElements.define("nss-scrolling-bar", NSSScrollingBar);
}
